import { ReviewResponse, CoverLetterResponse, InterviewPrepResponse, CommunicationsResponse, RewriteResumeResponse, Resume } from '@/api/types';
import { auth } from '@/services/firebase';
import localforage from 'localforage';

async function callRpc(action: string, payload: any, signal?: AbortSignal) {
  let idToken;
  if (auth && auth.currentUser) {
    idToken = await auth.currentUser.getIdToken();
  } else {
    if (import.meta.env.VITE_BYPASS_AUTH === 'true') {
      idToken = 'bypass-token';
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const response = await fetch('/api/ai', {
    method: 'POST',
    headers,
    signal,
    body: JSON.stringify({
      action,
      payload
    })
  });

  if (!response.ok) {
    let errorMsg = `Server error: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMsg = typeof errorData.error === 'object' ? JSON.stringify(errorData.error) : errorData.error;
      }
    } catch (e) {}
    
    if (response.status === 401) {
      window.dispatchEvent(new Event('app:require-login'));
      if (errorMsg && errorMsg.includes('Invalid session')) {
        throw new Error(errorMsg);
      }
      throw new Error(`Authentication failed. ${errorMsg}`);
    }
    throw new Error(errorMsg);
  }

  return response.json();
}

export async function analyzeResume(
  resume: Resume, jobText: string, company: string, position: string, requirements: string, targetLocation: string, jobType?: string, workModel?: string, visaSponsorship?: string, relocation?: string, signal?: AbortSignal
): Promise<ReviewResponse> {
  return callRpc('analyzeResume', { resume, jobText, company, position, requirements, targetLocation, jobType, workModel, visaSponsorship, relocation }, signal);
}

export async function generateCoverLetter(
  resume: Resume, jobText: string, company: string, position: string, requirements: string, targetLocation: string, jobType?: string, workModel?: string, visaSponsorship?: string, relocation?: string, signal?: AbortSignal
): Promise<CoverLetterResponse> {
  return callRpc('generateCoverLetter', { resume, jobText, company, position, requirements, targetLocation, jobType, workModel, visaSponsorship, relocation }, signal);
}

export async function generateInterviewPrep(resume: Resume, jobText: string, company: string, position: string, signal?: AbortSignal): Promise<InterviewPrepResponse> {
  return callRpc('generateInterviewPrep', { resume, jobText, company, position }, signal);
}

export async function generateCommunications(resume: Resume, jobText: string, company: string, position: string, emailType?: string, signal?: AbortSignal): Promise<CommunicationsResponse> {
  return callRpc('generateCommunications', { resume, jobText, company, position, emailType }, signal);
}

export async function generateRefinedResume(resume: Resume, jobText: string, recommendations: any[], customInstructions?: string, currentResumeData?: any, signal?: AbortSignal): Promise<RewriteResumeResponse> {
  return callRpc('generateRefinedResume', { resume, jobText, recommendations, customInstructions, currentResumeData }, signal);
}

export async function searchJobDescriptionWithAI(jobLink: string, signal?: AbortSignal): Promise<{text: string, usage: any}> {
  return callRpc('searchJobDescriptionWithAI', { jobLink }, signal);
}

export async function cleanJobDescriptionWithAI(rawText: string, signal?: AbortSignal): Promise<{text: string, usage: any}> {
  return callRpc('cleanJobDescriptionWithAI', { rawText }, signal);
}

export async function extractJobMetadata(jobText: string, signal?: AbortSignal): Promise<{data: any, usage: any}> {
  return callRpc('extractJobMetadata', { jobText }, signal);
}

export async function searchForJobs(userRequirements: string, signal?: AbortSignal): Promise<{data: any, usage: any}> {
  return callRpc('searchForJobs', { userRequirements }, signal);
}

